import React, { useState } from "react";
import {
  Send,
  Mail,
  Phone,
  MapPin,
  Loader2,
  CheckCircle,
  XCircle,
} from "lucide-react";
import { useLanguage } from "../../context/LanguageProvider";
import { useMutation } from "@tanstack/react-query";
import {
  submitContactForm,
  ContactFormData,
  ContactFormSubmissionResponse,
} from "../../api/api";

const ContactSection: React.FC = () => {
  const { t } = useLanguage();

  const [formData, setFormData] = useState<ContactFormData>({
    name: "",
    email: "",
    message: "",
  });

  // Use useMutation hook
  const mutation: any = useMutation<
    ContactFormSubmissionResponse,
    Error,
    ContactFormData
  >({
    mutationFn: submitContactForm,
    onSuccess: (data) => {
      console.log("Contact form submitted successfully:", data);
      setSubmittedMessage(t("contact_form_success_message"));
      setSubmitted(true);
      setFormData({ name: "", email: "", message: "" });

      setTimeout(() => {
        setSubmitted(false);
        setSubmittedMessage("");
      }, 5000);
    },
    onError: (error) => {
      console.error("Error submitting contact form:", error);

      setErrorMessage(error.message);
      setSubmitted(true);
      setTimeout(() => {
        setSubmitted(false);
        setErrorMessage("");
      }, 10000);
    },
  });

  const [submitted, setSubmitted] = useState(false);
  const [submittedMessage, setSubmittedMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    mutation.mutate(formData);
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <section className="py-24 bg-gray-50 relative" id="contact">
      {/* Abstract background shapes - Kept as is */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-1/3 right-1/4 w-64 h-64 rounded-full bg-primary blur-3xl"></div>
        <div className="absolute bottom-1/3 left-1/4 w-96 h-96 rounded-full bg-secondary blur-3xl"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="max-w-3xl mx-auto text-center mb-16">
          <div className="inline-flex items-center px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium mb-4">
            <span className="flex h-2 w-2 rounded-full bg-primary mr-2"></span>
            {t("contact_badge")}
          </div>

          <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4">
            {t("contact_title_part1")}{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-secondary">
              {t("contact_title_part2")}
            </span>
          </h2>
          <p className="text-xl text-gray-600">{t("contact_description")}</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <div className="bg-white rounded-xl shadow-md p-8">
              {/* Conditional rendering for success/error messages */}
              {submitted && (submittedMessage || errorMessage) ? (
                <div
                  className={`text-center py-12 ${
                    errorMessage ? "text-red-600" : "text-green-600"
                  }`}
                >
                  <div
                    className={`inline-flex items-center justify-center w-16 h-16 rounded-full mb-4 ${
                      errorMessage
                        ? "bg-red-100 text-red-500"
                        : "bg-green-100 text-green-500"
                    }`}
                  >
                    {errorMessage ? (
                      <XCircle className="w-8 h-8" />
                    ) : (
                      <CheckCircle className="w-8 h-8" />
                    )}
                  </div>
                  <h3 className="text-2xl font-bold mb-2">
                    {errorMessage
                      ? t("contact_form_error_title") || "Submission Failed"
                      : t("contact_form_success_title")}
                  </h3>{" "}
                  {/* Added fallback title for error */}
                  <p className="text-gray-600">
                    {errorMessage || submittedMessage}{" "}
                    {/* Display error or success message */}
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label
                        htmlFor="name"
                        className="block text-sm font-medium text-gray-700 mb-1"
                      >
                        {t("contact_form_name_label")}
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        className="block w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                        placeholder={t("contact_form_name_placeholder")}
                        required
                        disabled={mutation.isLoading} // Disable input while submitting
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="email"
                        className="block text-sm font-medium text-gray-700 mb-1"
                      >
                        {t("contact_form_email_label")}
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        className="block w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                        placeholder={t("contact_form_email_placeholder")}
                        required
                        disabled={mutation.isLoading} // Disable input while submitting
                      />
                    </div>
                  </div>
                  <div>
                    <label
                      htmlFor="message"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      {t("contact_form_message_label")}
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      rows={5}
                      className="block w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                      placeholder={t("contact_form_message_placeholder")}
                      required
                      disabled={mutation.isLoading} // Disable input while submitting
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={mutation.isLoading} // Disable button while submitting
                    className="w-full bg-gradient-to-r from-primary to-secondary text-white py-4 px-6 rounded-lg font-medium shadow-md shadow-primary/20 hover:shadow-lg hover:shadow-primary/30 transition-all duration-300 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed" // Added disabled styles
                  >
                    {mutation.isLoading ? (
                      <>
                        <Loader2 className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" />{" "}
                        {/* Use Loader2 icon */}
                        {t("contact_form_button_sending")}
                      </>
                    ) : (
                      <>
                        {t("contact_form_button_sendMessage")}
                        <Send className="ml-2 h-5 w-5" />
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>

          {/* Contact Information - Kept as is, using translations */}
          <div>
            <div className="bg-white rounded-xl shadow-md p-8 h-full">
              <h3 className="text-xl font-bold mb-6">
                {t("contact_info_title")}
              </h3>

              <div className="space-y-6">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <div className="flex items-center justify-center h-10 w-10 rounded-full bg-primary/10 text-primary">
                      <Mail className="h-5 w-5" />
                    </div>
                  </div>
                  <div className="ml-4">
                    <h4 className="text-sm font-medium text-gray-900">
                      {t("contact_info_email_label")}
                    </h4>
                    <a
                      href={`mailto:${t("contact_info_email_address")}`}
                      className="text-primary hover:underline"
                    >
                      {t("contact_info_email_address")}
                    </a>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <div className="flex items-center justify-center h-10 w-10 rounded-full bg-primary/10 text-primary">
                      <Phone className="h-5 w-5" />
                    </div>
                  </div>
                  <div className="ml-4">
                    <h4 className="text-sm font-medium text-gray-900">
                      {t("contact_info_phone_label")}
                    </h4>
                    <a
                      href={`tel:${t("contact_info_phone_number").replace(
                        /\s/g,
                        ""
                      )}`}
                      className="text-gray-600 hover:text-primary"
                    >
                      {t("contact_info_phone_number")}
                    </a>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <div className="flex items-center justify-center h-10 w-10 rounded-full bg-primary/10 text-primary">
                      <MapPin className="h-5 w-5" />
                    </div>
                  </div>
                  <div className="ml-4">
                    <h4 className="text-sm font-medium text-gray-900">
                      {t("contact_info_office_label")}
                    </h4>
                    <p className="text-gray-600">
                      {t("contact_info_office_address_line1")}
                      <br />
                      {t("contact_info_office_address_line2")}
                      <br />
                      {t("contact_info_office_address_line3")}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
